import { useCallback, useEffect, useState } from 'react';
import {
  type Application,
  type CorpusReadiness,
  type JobRecord,
  getCorpusReadiness,
  getCorpusRoles,
  getJobs,
  getTargetRoles,
  listApplications,
} from './api';

/**
 * Wave-1 C1 — the Home tab's REAL data hook. Replaces placeholderHome/
 * placeholderJobs (lib/placeholderData.ts) as Home.tsx's data source; every
 * field here traces to an existing, already-shipped GET route (NO backend
 * changes, per the lane's own law):
 *
 *   - displayName   -> useAuth() (App.tsx already reads this; Home.tsx reads
 *                      its own copy since this hook has no auth dependency)
 *   - roleCount     -> GET /corpus/roles (getCorpusRoles, the SAME read
 *                      useOnboardingGate/useCorpusCanvas already call)
 *   - readiness     -> GET /corpus/readiness (getCorpusReadiness, additive to
 *                      this app's api.ts this lane) — a deterministic,
 *                      code-computed 0..100 "story told" score + a real
 *                      gold-moment count, never a model self-report. A
 *                      0-corpus user gets score:0 by construction, not a
 *                      special-cased zero this hook invents.
 *   - applications  -> GET /applications (listApplications, the SAME read
 *                      useApplicationsBoard already calls for the Track tab)
 *                      — "Jobs you're tailoring" renders these VERBATIM
 *                      (jobTitle/jobCompany only; fit is not carried on this
 *                      row — see Home.tsx's RealHome doc comment for why no
 *                      score renders here), never the placeholder's invented
 *                      job rows (the exact fabricated names are deliberately
 *                      not repeated in source — see Home.tsx's top-of-file
 *                      doc comment).
 *   - targetRoles   -> GET /presence/target-roles (getTargetRoles, the SAME
 *                      route Get Found already reads) — LANE U1's
 *                      QuickStartGuide step-3 done-check ("declare what you
 *                      want to be found for") reads this count, never a
 *                      fabricated flag.
 *   - jobs          -> GET /jobs (getJobs, the SAME route the Jobs/Discover
 *                      tab's useJobsInbox reads, default fit sort) — LANE
 *                      U1's welcome-back banner counts each row's own
 *                      server-computed `new_since_last_visit` boolean
 *                      (jobs.routes.ts's newSinceLastVisitFragment) via
 *                      welcomeBack.ts's countNewSinceLastVisit. This is a
 *                      normal DB read (the same query class as readiness/
 *                      applications above), NOT the heavier POST
 *                      /watchlist/poll external-board sweep — that stays
 *                      strictly behind the "Check now" tap (useWelcomeBackPoll),
 *                      never auto-fired here.
 *
 * Each of the five reads is independent (own try/catch via Promise.allSettled)
 * so one failing (e.g. a transient /corpus/readiness 500) never blanks the
 * others — mirrors useJobsInbox/useApplicationsBoard's own "never let one
 * failed read wedge the whole screen" discipline. A failed read leaves its
 * slice at its initial null/[]/loading-settled state — genuinely "unknown,"
 * rendered as an honest loading/absent state, never coerced into a
 * fabricated value.
 */

export interface HomeDataState {
  loading: boolean;
  /** Real GET /corpus/roles length, or null while unresolved/unknown (a
   *  failed read never coerces to 0 — see useOnboardingGate's own
   *  "unknown, not empty" doc comment, which this hook mirrors). */
  roleCount: number | null;
  readiness: CorpusReadiness | null;
  readinessError: string | null;
  applications: Application[];
  applicationsError: string | null;
  /** Real GET /presence/target-roles length, or null while unresolved/
   *  unknown — same "unknown, not empty" discipline as roleCount above. */
  targetRoleCount: number | null;
  targetRolesError: string | null;
  /** Real GET /jobs rows (default fit sort, same as the Jobs tab's own
   *  default) — read here ONLY for their own server-computed
   *  new_since_last_visit flag (see this file's own top-of-file doc
   *  comment); Home never re-renders this as a job list. Empty array while
   *  unresolved/failed — countNewSinceLastVisit(([])) is honestly 0, never a
   *  fabricated positive count. */
  jobs: JobRecord[];
  jobsError: string | null;
}

const INITIAL_STATE: HomeDataState = {
  loading: true,
  roleCount: null,
  readiness: null,
  readinessError: null,
  applications: [],
  applicationsError: null,
  targetRoleCount: null,
  targetRolesError: null,
  jobs: [],
  jobsError: null,
};

function settledErrorMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : String(reason);
}

/**
 * LANE U1 — pulled out of the hook body (previously inline in the
 * useCallback below) so this five-way Promise.allSettled reduction is
 * directly unit-testable without React/RTL, same "extract the pure decision"
 * house idiom onboardingStep.ts/homeRouteGate.ts/quickStartGuide.ts already
 * establish for this app. Takes the raw settled results, returns the exact
 * HomeDataState useHomeData's setState call used to build inline — byte-for-
 * byte the same shape, zero behavior change, only a name for the logic.
 */
export function reduceHomeDataSettled(
  rolesResult: PromiseSettledResult<Awaited<ReturnType<typeof getCorpusRoles>>>,
  readinessResult: PromiseSettledResult<Awaited<ReturnType<typeof getCorpusReadiness>>>,
  applicationsResult: PromiseSettledResult<Awaited<ReturnType<typeof listApplications>>>,
  targetRolesResult: PromiseSettledResult<Awaited<ReturnType<typeof getTargetRoles>>>,
  jobsResult: PromiseSettledResult<Awaited<ReturnType<typeof getJobs>>>,
): HomeDataState {
  return {
    loading: false,
    roleCount: rolesResult.status === 'fulfilled' ? rolesResult.value.length : null,
    readiness: readinessResult.status === 'fulfilled' ? readinessResult.value : null,
    readinessError: readinessResult.status === 'rejected' ? settledErrorMessage(readinessResult.reason) : null,
    applications: applicationsResult.status === 'fulfilled' ? applicationsResult.value : [],
    applicationsError:
      applicationsResult.status === 'rejected' ? settledErrorMessage(applicationsResult.reason) : null,
    targetRoleCount: targetRolesResult.status === 'fulfilled' ? targetRolesResult.value.length : null,
    targetRolesError:
      targetRolesResult.status === 'rejected' ? settledErrorMessage(targetRolesResult.reason) : null,
    jobs: jobsResult.status === 'fulfilled' ? jobsResult.value.jobs : [],
    jobsError: jobsResult.status === 'rejected' ? settledErrorMessage(jobsResult.reason) : null,
  };
}

export function useHomeData() {
  const [state, setState] = useState<HomeDataState>(INITIAL_STATE);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));

    const [rolesResult, readinessResult, applicationsResult, targetRolesResult, jobsResult] =
      await Promise.allSettled([
        getCorpusRoles(),
        getCorpusReadiness(),
        listApplications(),
        getTargetRoles(),
        getJobs(),
      ]);

    setState(
      reduceHomeDataSettled(rolesResult, readinessResult, applicationsResult, targetRolesResult, jobsResult),
    );
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
}
